import { Stack, TableBody, TableCell, TableRow } from '@mui/material';
import { useRef } from 'react';
import { SourceReference } from '../../../common/SourceReference';
import { formatCurrency } from '../../lib/formatCurrency';
import { formatSynaDate } from '../../lib/formatSynaDate';
import { Omfragad } from '../../model/GrunduppgifterDto';
import { ForetagTable } from '../ForetagTable/ForetagTable';
import { PrintButton } from '../PrintButton/PrintButton';
import { ForetagAddressRow } from './ForetagAddressRow';
import { ForetagPersonOrganization } from './ForetagPersonOrganization';
import styles from './ForetagSearchResult.module.css';

const formatRakenskapsarEntry = (entry: string) => {
  if (!entry || entry.length !== 4) {
    return entry;
  }
  const date = new Date();
  date.setDate(Number.parseInt(entry.substring(2)));
  date.setMonth(Number.parseInt(entry.substring(0, 2)) - 1);
  return date.toLocaleDateString('sv-SE', {
    month: 'long',
    day: 'numeric',
  });
};

interface ForetagSearchResultProps {
  foretag: Omfragad;
}

enum Adresstyp {
  Postadress = 'padr',
  Besoksadress = 'besok',
  Satesadress = 'sate',
  Folkbokforingsadress = 'fbf',
  SarskildPostadress = 'sps',
  UtlandskSarskildPostadress = 'spu',
}

export const ForetagSearchResult: React.FC<ForetagSearchResultProps> = ({
  foretag,
}) => {
  if (!foretag) {
    return <></>;
  }

  const printWrapper = useRef<HTMLDivElement>();
  const postadress = foretag.adresslista.adress?.find(
    (adr) => adr.typ === Adresstyp.Postadress
  );
  const besoksadress = foretag.adresslista.adress?.find(
    (adr) => adr.typ === Adresstyp.Besoksadress
  );
  const satesadress = foretag.adresslista.adress?.find(
    (adr) => adr.typ === Adresstyp.Satesadress
  );
  const folkbokforingsadress = foretag.adresslista.adress?.find(
    (adr) => adr.typ === Adresstyp.Folkbokforingsadress
  );
  const sarskildPostadress = foretag.adresslista.adress?.find(
    (adr) => adr.typ === Adresstyp.SarskildPostadress
  );
  const utlandskSarskildPostadress = foretag.adresslista.adress?.find(
    (adr) => adr.typ === Adresstyp.UtlandskSarskildPostadress
  );
  const name = foretag.namnlista.namn?.[0].value;
  const phoneNumber = foretag.telefon?.value;
  const ftgform = foretag.ftgform?.value;
  const bokslut = foretag.bokslut?.bokslutsinfo;

  return (
    <Stack className={styles.foretagSearchResult}>
      <div ref={printWrapper}>
        <h3>Företag</h3>
        <div className="grunduppgifterPrint">
          <ForetagTable className={styles.grunduppgifter}>
            <TableBody>
              <TableRow>
                <TableCell>Organisationsnummer</TableCell>
                <TableCell>
                  <ForetagPersonOrganization
                    persOrgNr={foretag.id}
                    note={foretag.not}
                  />
                </TableCell>
              </TableRow>
              {name && (
                <TableRow>
                  <TableCell>Namn</TableCell>
                  <TableCell>{name}</TableCell>
                </TableRow>
              )}
              {phoneNumber && (
                <TableRow>
                  <TableCell>Telefonnummer</TableCell>
                  <TableCell>{phoneNumber}</TableCell>
                </TableRow>
              )}
              <ForetagAddressRow address={postadress} title="Postadress" />
              <ForetagAddressRow address={besoksadress} title="Besöksadress" />
              <ForetagAddressRow address={satesadress} title="Säte" />
              <ForetagAddressRow
                address={folkbokforingsadress}
                title="Folkbokföringsadress"
              />
              <ForetagAddressRow
                address={sarskildPostadress}
                title="Särskild postadress"
              />
              <ForetagAddressRow
                address={utlandskSarskildPostadress}
                title="Utländsk särskild postadress"
              />
            </TableBody>
          </ForetagTable>
          <h3>Registrering</h3>
          <ForetagTable className={styles.grunduppgifter}>
            <TableBody>
              <TableRow>
                <TableCell>Företagsform</TableCell>
                <TableCell>{ftgform}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Registreringsdatum</TableCell>
                <TableCell>{formatSynaDate(foretag.regdatum)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Bolagets säte</TableCell>
                <TableCell>
                  {foretag.bolsate?.replace(/\s*\/\s*/, '/')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Ledning reg.datum</TableCell>
                <TableCell>
                  {formatSynaDate(foretag.styregdatum) || 'Saknas'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Aktiekapital</TableCell>
                <TableCell>
                  {foretag.aktiekapital?.value
                    ? `${formatCurrency(foretag.aktiekapital.value, 0.01)} ${
                        foretag.aktiekapital.valuta || ''
                      }`
                    : 'Saknas'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Räkenskapsår</TableCell>
                <TableCell>
                  {foretag.rakenskapsar
                    ? `${formatRakenskapsarEntry(
                        foretag.rakenskapsar.from
                      )} - ${formatRakenskapsarEntry(foretag.rakenskapsar.tom)}`
                    : 'Saknas'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Tidigare namn</TableCell>
                <TableCell>{foretag.histnamn || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Koncernmoder</TableCell>
                <TableCell>{foretag.koncernmoder?.not || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Branschkod (SNI)</TableCell>
                <TableCell>
                  {foretag.branschlista?.bransch?.[0].branschkod}{' '}
                  {foretag.branschlista?.bransch?.[0].branschtext}
                </TableCell>
              </TableRow>
            </TableBody>
          </ForetagTable>
          <h3>Senaste bokslut</h3>
          <ForetagTable className={styles.grunduppgifter}>
            <TableBody>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>
                  {bokslut?.periodFr || bokslut?.periodTo
                    ? `${formatSynaDate(bokslut?.periodFr)} - ${formatSynaDate(
                        bokslut?.periodTo
                      )}`
                    : 'Saknas'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Godkänt av Bolagsverket</TableCell>
                <TableCell>
                  {formatSynaDate(bokslut?.godkdatum) || 'Saknas'}
                </TableCell>
              </TableRow>
            </TableBody>
          </ForetagTable>
        </div>
        <SourceReference
          sourceName="Syna kredit- och affärsinformation"
          style={{ marginTop: '16px' }}
        />
      </div>
      <PrintButton printContents={printWrapper} />
    </Stack>
  );
};
